// ----------------------------------------------------------------------------
// AIFF / AIFC decoder — converts AIFF ArrayBuffer to Web Audio AudioBuffer
// ----------------------------------------------------------------------------
// Chrome/Firefox don't support AIFF in decodeAudioData (Safari does).
// This module parses the IFF structure (FORM...AIFF/AIFC) and decodes the
// PCM audio data into a format compatible with Web Audio.
//
// Supported:
//   - AIFF (uncompressed PCM, big-endian, 8/16/24/32-bit)
//   - AIFC with 'NONE' and 'twos' compression types (same as AIFF PCM)
//   - AIFC with 'sowt' compression (little-endian PCM)
//
// Returns an AudioBuffer, or null if the format is unsupported.
// ----------------------------------------------------------------------------

/**
 * Decode an AIFF/AIFC ArrayBuffer into an AudioBuffer.
 * @param {AudioContext} ctx - Web Audio context (for sample rate + creating buffer)
 * @param {ArrayBuffer} arrayBuffer - raw AIFF file data
 * @returns {Promise<AudioBuffer|null>}
 */
async function decodeAIFF(ctx, arrayBuffer) {
   const dv = new DataView(arrayBuffer);

   // Check FORM magic
   const magic = read4cc(dv, 0);
   if (magic !== 'FORM') return null;

   // Check AIFF or AIFC subtype
   const subtype = read4cc(dv, 8);
   if (subtype !== 'AIFF' && subtype !== 'AIFC') return null;

   let numChannels = 0;
   let numSampleFrames = 0;
   let sampleSize = 0;
   let sampleRate = 0;
   let compressionType = null;
   let soundData = null; // Uint8Array of raw audio bytes
   let soundOffset = 0; // offset within soundData where audio starts
   let soundBlockSize = 0; // block size (for block-aligned data)

   // Parse chunks starting at offset 12
   let offset = 12;
   const end = dv.byteLength;

   while (offset + 8 <= end) {
      const chunkId = read4cc(dv, offset);
      const chunkSize = dv.getUint32(offset + 4); // big-endian by default
      const dataStart = offset + 8;

      if (chunkId === 'COMM') {
         numChannels = dv.getUint16(dataStart);
         numSampleFrames = dv.getUint32(dataStart + 2);
         sampleSize = dv.getUint16(dataStart + 6);
         sampleRate = readExtendedFloat(dv, dataStart + 8);
         if (subtype === 'AIFC') {
            compressionType = read4cc(dv, dataStart + 18);
         }
      } else if (chunkId === 'SSND') {
         soundOffset = dv.getUint32(dataStart); // offset to sound data
         soundBlockSize = dv.getUint32(dataStart + 4); // block size
         const audioStart = dataStart + 8 + soundOffset;
         const audioEnd = Math.min(dataStart + chunkSize, end);
         soundData = new Uint8Array(arrayBuffer, audioStart, audioEnd - audioStart);
      }

      // Chunks are padded to even size
      offset = dataStart + chunkSize;
      if (chunkSize & 1) offset++;
   }

   if (!soundData || numChannels === 0 || sampleSize === 0 || numSampleFrames === 0) {
      return null;
   }

   // Check compression type
   const isLittleEndian = (compressionType === 'sowt');
   if (compressionType && compressionType !== 'NONE' && compressionType !== 'twos' && compressionType !== 'sowt') {
      console.warn('[aiff-decoder] unsupported compression:', compressionType);
      return null;
   }

   // Create AudioBuffer
   const buffer = ctx.createBuffer(numChannels, numSampleFrames, sampleRate);

   // Decode PCM samples
   const bytesPerSample = sampleSize / 8;
   const frameSize = bytesPerSample * numChannels;
   const littleEndian = isLittleEndian;

   for (let ch = 0; ch < numChannels; ch++) {
      const channelData = buffer.getChannelData(ch);
      for (let i = 0; i < numSampleFrames; i++) {
         const frameOffset = i * frameSize + ch * bytesPerSample;
         if (frameOffset + bytesPerSample > soundData.length) break;

         let sample = 0;
         if (sampleSize === 8) {
            // 8-bit samples are unsigned in AIFF
            sample = (soundData[frameOffset] - 128) / 128;
         } else if (sampleSize === 16) {
            const dv2 = new DataView(soundData.buffer, soundData.byteOffset + frameOffset, 2);
            sample = dv2.getInt16(0, littleEndian) / 32768;
         } else if (sampleSize === 24) {
            const b0 = soundData[frameOffset];
            const b1 = soundData[frameOffset + 1];
            const b2 = soundData[frameOffset + 2];
            let val;
            if (littleEndian) {
               val = (b2 << 16) | (b1 << 8) | b0;
            } else {
               val = (b0 << 16) | (b1 << 8) | b2;
            }
            if (val & 0x800000) val |= 0xFF000000; // sign extend
            sample = val / 8388608;
         } else if (sampleSize === 32) {
            const dv2 = new DataView(soundData.buffer, soundData.byteOffset + frameOffset, 4);
            sample = dv2.getInt32(0, littleEndian) / 2147483648;
         }
         channelData[i] = sample;
      }
   }

   return buffer;
}

// Read a 4-character code from a DataView at the given offset
function read4cc(dv, offset) {
   let s = '';
   for (let i = 0; i < 4; i++) {
      s += String.fromCharCode(dv.getUint8(offset + i));
   }
   return s;
}

// Read an 80-bit IEEE 754 extended float (big-endian) — used for sample rate in AIFF
function readExtendedFloat(dv, offset) {
   const exponent = ((dv.getUint8(offset) & 0x7F) << 8) | dv.getUint8(offset + 1);
   let mantissa = 0;
   for (let i = 2; i < 10; i++) {
      mantissa = mantissa * 256 + dv.getUint8(offset + i);
   }
   return mantissa * Math.pow(2, exponent - 16383 - 63);
}

/**
 * Check if an ArrayBuffer looks like an AIFF file.
 * @param {ArrayBuffer} arrayBuffer
 * @returns {boolean}
 */
function isAIFF(arrayBuffer) {
   if (arrayBuffer.byteLength < 12) return false;
   const dv = new DataView(arrayBuffer);
   const magic = read4cc(dv, 0);
   if (magic !== 'FORM') return false;
   const subtype = read4cc(dv, 8);
   return subtype === 'AIFF' || subtype === 'AIFC';
}

module.exports = { decodeAIFF, isAIFF };
